import React, { useRef, useEffect, useState } from 'react';
import { ResizableBox } from 'react-resizable';
import { useDispatch, useSelector } from 'react-redux';
import {
  closeCashbot,
  addCashbotMessage,
  processCashbotQuery,
  clearCashbotMessages,
  loadCashbotHistory,
} from '../../../../../app/store/slices/adminSlice';
import CashbotMessage from './CashbotMessage';
import CashbotInput from './CashbotInput';
import CashbotTypingIndicator from './CashbotTypingIndicator';
import useClickOutside from '../../../../shared/hooks/useClickOutside'
import 'react-resizable/css/styles.css';

const CashbotChat = ({
  messages = [],
  isTyping = false,
  isOpen = true,
  onSendMessage,
  onClose,
  onClearHistory,
  context = 'admin',
  canvasId = null,
  canvasName = ''
}) => {
  const messagesEndRef = useRef(null);
  const cashbotRef = useRef(null);
  useClickOutside(cashbotRef, onClose, isOpen);

  const getViewportSize = () => {
    if (typeof window === 'undefined') {
      return { width: 1200, height: 800 };
    }
    return { width: window.innerWidth, height: window.innerHeight };
  };

  const getDefaultSize = () => {
    const { width, height } = getViewportSize();
    const defaultWidth = Math.min(384, Math.floor(width * 0.95));
    const defaultHeight = Math.min(Math.floor(height * 0.8), 620);

    return {
      width: Math.max(320, defaultWidth),
      height: Math.max(360, defaultHeight)
    };
  };

  const [size, setSize] = useState(() => getDefaultSize());
  const [maxConstraints, setMaxConstraints] = useState(() => {
    const { width, height } = getViewportSize();
    return [
      Math.max(320, Math.floor(width * 0.95)),
      Math.max(360, Math.floor(height * 0.85))
    ];
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [isOpen, messages, isTyping]);

  useEffect(() => {
    const handleResize = () => {
      const { width, height } = getViewportSize();
      const nextMaxWidth = Math.max(320, Math.floor(width * 0.95));
      const nextMaxHeight = Math.max(360, Math.floor(height * 0.85));

      setMaxConstraints([nextMaxWidth, nextMaxHeight]);
      setSize((prev) => ({
        width: Math.min(prev.width, nextMaxWidth),
        height: Math.min(prev.height, nextMaxHeight)
      }));
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, []);
  if (!isOpen) return null;

  return (
  <div ref={cashbotRef} className="inline-block">
    <ResizableBox
      width={size.width}
      height={size.height}
      minConstraints={[320, 360]}
      maxConstraints={maxConstraints}
      resizeHandles={['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw']}
      onResizeStop={(_, data) => setSize({ width: data.size.width, height: data.size.height })}
      className="relative"
    >
    <div 
      className="
         flex flex-col
          w-full h-full
          bg-white rounded-t-lg md:rounded-lg shadow-2xl border
          transition-all
      ">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-btn-primary text-white rounded-t-lg">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-btn-primary rounded-full flex items-center justify-center">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-sm">
              CashBot {context === 'admin' ? 'Admin' : ''} Assistant
            </h3>
            <p className="text-xs opacity-90">
              {context === 'admin' 
                ? 'Your Admin AI Helper' 
                : `Canvas: ${canvasName || 'Not Selected'}`}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={onClearHistory}
            className="p-1 hover:bg-btn-primary rounded"
            title="Clear chat history"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
          <button
            onClick={onClose}
            className="p-1 hover:bg-btn-primary rounded"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="text-center text-gray-500 mt-8">
            <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p className="text-sm">Start a conversation with CashBot!</p>
            <p className="text-xs mt-1">
              {context === 'admin' 
                ? 'Ask about analytics, users, or create widgets' 
                : 'Ask about your finances or create dashboard widgets'}
            </p>
          </div>
        ) : (
          messages.map((message) => (
            <CashbotMessage 
              key={message.id} 
              message={message} 
              context={context}
              canvasId={context === 'user' ? canvasId : null}
              canvasName={canvasName}
            />
          ))
        )}
        
        {isTyping && <CashbotTypingIndicator />}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t">
        <CashbotInput 
        onSendMessage={onSendMessage}
         disabled={isTyping}
         context={context}
          />
      </div>
      </div>
    </ResizableBox>
  </div>
  );
};

// For backward compatibility, a wrapper that uses admin state
const AdminCashbotChatWrapper = () => {
  const dispatch = useDispatch();
  const { isOpen, messages, isTyping } = useSelector((state) => state.admin.cashbot);
  const userId = useSelector((state) => state.auth?.user?._id);

  const getStorageKey = (id) => `cashbot_history_${id}`;

  useEffect(() => {
    // Load chat history from localStorage on mount only if user is logged in
    if (userId) {
      const storageKey = getStorageKey(userId);
      const savedMessages = localStorage.getItem(storageKey);
      if (savedMessages) {
        try {
          const parsedMessages = JSON.parse(savedMessages);
          dispatch(loadCashbotHistory(parsedMessages));
        } catch (error) {
          console.error('Error loading chat history:', error);
        }
      }
    }
  }, [dispatch, userId]);

  useEffect(() => {
    // Save messages to localStorage whenever messages change (only if user is logged in)
    if (messages.length > 0 && userId) {
      const storageKey = getStorageKey(userId);
      localStorage.setItem(storageKey, JSON.stringify(messages));
    }
  }, [messages, userId]);

  const handleSendMessage = async (message) => {
    // Add user message
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    };
      dispatch(addCashbotMessage(userMessage));
      await dispatch(processCashbotQuery(message)).unwrap();
  };

  const handleClearHistory = () => {
    dispatch(clearCashbotMessages());
    if (userId) {
      const storageKey = getStorageKey(userId);
      localStorage.removeItem(storageKey);
    }
  };

  return (
    <CashbotChat
      messages={messages}
      isTyping={isTyping}
      isOpen={isOpen}
      onSendMessage={handleSendMessage}
      onClose={() => dispatch(closeCashbot())}
      onClearHistory={handleClearHistory}
      context="admin"
    />
  );
};

// Export the component
export default function(props) {
  // If called with explicit props, use the generic component
  if (Object.keys(props).length > 0) {
    return <CashbotChat {...props} />;
  }
  
  // Otherwise use the admin wrapper
  return <AdminCashbotChatWrapper />;
}